import {api} from "@/shared/api";

import {BasicDataTable, TypedColumnSettings} from "@/shared/ui/basicDataTable";
import {SelectableDataTable, SelectableDataTableConfig} from "@/shared/ui/selectableDataTable";
import {DataTableBulkSelector} from "@/shared/ui/dataTableBulkSelector";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {ExternalSyncUser, User} from "@/shared/timr";
import type {ExternalUsersListResponse} from "@/shared/timr";
import Modal = BootstrapModal.Modal;

export interface ExternalUsersBulkCommand {
	allSelected: boolean;
	idsInclude: string[] | string;
	idsExclude: string[] | string;
	integration: string;
}

export class ExternalUserSyncDialog {
	public static EVENT_USERS_CHANGED = "event_users_changed";

	private readonly dom: JQuery;
	private modal: BootstrapModal.Modal;
	private createTable: ExternalUsersCreateDataTable;
	private linkTable: ExternalUsersLinkDataTable;
	private updateTable: ExternalUsersUpdateDataTable;

	private createUsers: ExternalSyncUser[];
	private linkUsers: ExternalSyncUser[];
	private updateUsers: ExternalSyncUser[];
	private selectedIntegration: string;

	constructor() {
		this.dom = $("#externalUserSyncDialog");
		this.modal = new Modal({
			message: this.dom,
			size: "modal-full",
			buttons: [
				{
					text: "dialog.close",
					css: "btn btn-default"
				}
			],
			onOpen: () => {
				this.loadData();
			},
			onClose: () => {
				this.createTable.bulkSelector.selectNone();
			}
		});

		this.createTable = new ExternalUsersCreateDataTable(() => this.createUsers, {
			element: this.dom.find("#createUsersTable"),
			allowSelect: true,
			excludeElementsDblclickEvent: [".select-checkbox"],
			actionButton: this.dom.find("#createExternalUsersButton"),
			filterInput: this.dom.find("#createExternalUsersFilter"),
			allSelectedMessage: "user.externalUserSyncDialog.create.bulkselection.all",
			nSelectedMessage: "user.externalUserSyncDialog.create.bulkselection.n",
			externalUserActionMessage: "user.externalUserSyncDialog.create.bulkselection",
			externalUserActionRequest: users => this.createExternalUsers(users) // add single user via table button
		}, this);

		this.linkTable = new ExternalUsersLinkDataTable(() => this.linkUsers, {
			element: this.dom.find("#linkUsersTable"),
			allowSelect: true,
			excludeElementsDblclickEvent: [".select-checkbox"],
			actionButton: this.dom.find("#linkExternalUsersButton"),
			filterInput: this.dom.find("#linkExternalUsersFilter"),
			allSelectedMessage: "user.externalUserSyncDialog.link.bulkselection.all",
			nSelectedMessage: "user.externalUserSyncDialog.link.bulkselection.n",
			externalUserActionMessage: "user.externalUserSyncDialog.link.bulkselection",
			externalUserActionRequest: users => this.linkExternalUsers(users) // add single user via table button
		}, this);

		this.updateTable = new ExternalUsersUpdateDataTable(() => this.updateUsers, {
			element: this.dom.find("#updateExternalUsersTable"),
			filterInput: this.dom.find("#updateExternalUsersFilter"),
			allowSelect: false,
			excludeElementsDblclickEvent: [".select-checkbox"],
			externalUserActionMessage: "user.settings.connections.disconnect",
			externalUserActionRequest: users => this.unlinkExternalUsers(users) // add single user via table button
		}, this);

		this.dom.find("#linkExternalUsersButton").click(e => {
			const button = $(e.target);

			Button.setLoading(button);
			this.linkExternalUsers({
				allSelected: this.linkTable.bulkSelector.isAllSelected(),
				idsInclude: this.linkTable.bulkSelector.getIdsToInclude(),
				idsExclude: this.linkTable.bulkSelector.getIdsToExclude(),
				integration: this.selectedIntegration
			}).always(() => Button.resetLoading(button));
		});
		this.dom.find("#createExternalUsersButton").click(e => {
			const button = $(e.target);

			Button.setLoading(button);
			this.createExternalUsers({
				allSelected: this.createTable.bulkSelector.isAllSelected(),
				idsInclude: this.createTable.bulkSelector.getIdsToInclude(),
				idsExclude: this.createTable.bulkSelector.getIdsToExclude(),
				integration: this.selectedIntegration
			}).always(() => Button.resetLoading(button));
		});
		this.dom.find("#updateExternalUsersButton").click(this.updateExternalUsers);
	}

	public open() {
		this.modal.setTitle(messages.withArguments("user.externalUserSyncDialog.title", messages["integrations.type." + this.selectedIntegration]));
		this.modal.show();
	}

	public loadData(): JQuery.jqXHR<ExternalUsersListResponse> {
		this.createTable.toggleLoadingIndicator();
		this.linkTable.toggleLoadingIndicator();
		this.updateTable.toggleLoadingIndicator();

		return api("admin/users/external/list.json")
			.get({integration: this.selectedIntegration})
			.done((data: ExternalUsersListResponse) => {
				if (data.conflictMessages != null && data.conflictMessages.length > 0) {
					BootstrapModal.error({
						message: messages.withArguments("external.user.sync.error.multiple.users.linked", "<br>" + data.conflictMessages.join("<br>")),
						escapeText: false,
						size: "modal-lg"
					});
				}
				this.createUsers = data.createUsers;
				this.linkUsers = data.linkUsers;
				this.updateUsers = data.updateUsers;

				this.createTable.reload();
				this.linkTable.reload();
				this.updateTable.reload();
			})
			.fail(() => {
				this.createTable.toggleLoadingIndicator(false);
				this.linkTable.toggleLoadingIndicator(false);
				this.updateTable.toggleLoadingIndicator(false);
			});
	}

	private createExternalUsers(users: ExternalUsersBulkCommand): JQuery.jqXHR {
		return api("admin/users/external/create.json")
			.post(users)
			.done(data => {
				if (Array.isArray(data) && data.length > 0) {
					BootstrapModal.error({
						message: messages.withArguments("external.user.sync.export.error", data.length, data.join("<br>")),
						escapeText: false,
						size: "modal-lg"
					});
				}
			})
			.done(() => {
				$(this).triggerHandler(ExternalUserSyncDialog.EVENT_USERS_CHANGED);
				this.loadData();
			});
	}

	private linkExternalUsers(users: ExternalUsersBulkCommand): JQuery.jqXHR {
		return api("admin/users/external/link.json")
			.post(users)
			.done(() => {
				$(this).triggerHandler(ExternalUserSyncDialog.EVENT_USERS_CHANGED);
				this.loadData();
			});
	}

	private unlinkExternalUsers(users: ExternalUsersBulkCommand): JQuery.jqXHR {
		return api("admin/users/external/unlink.json")
			.post(users)
			.done(() => {
				$(this).triggerHandler(ExternalUserSyncDialog.EVENT_USERS_CHANGED);
				this.loadData();
			});
	}

	private updateExternalUsers = (e: JQuery.TriggeredEvent) => {
		const button = $(e.target as HTMLElement);

		Button.setLoading(button);
		api("/admin/users/external/triggerSync.json")
			.get({integration: this.getSelectedIntegration()})
			.done(() => {
				$(this).triggerHandler(ExternalUserSyncDialog.EVENT_USERS_CHANGED);
				this.loadData();
			})
			.always(() => Button.resetLoading(button));
	}

	public setIntegration(integration: string) {
		this.selectedIntegration = integration;
	}

	public getSelectedIntegration(): string {
		return this.selectedIntegration;
	}
}

interface ExternalUsersDataTableConfig extends SelectableDataTableConfig {
	actionButton?: JQuery;
	allSelectedMessage?: string;
	nSelectedMessage?: string;
	filterInput?: JQuery;
	externalUserActionRequest?: (externalUserCommand: ExternalUsersBulkCommand) => JQuery.jqXHR;
	externalUserActionMessage?: string;
}

abstract class ExternalUsersDataTable extends SelectableDataTable<ExternalSyncUser> {
	public static COLUMN_SIZE_EXTERNAL_USER_ACTION = "130px";

	constructor(source: () => ExternalSyncUser[], public config: ExternalUsersDataTableConfig, public dialog: ExternalUserSyncDialog) {
		super(source, config);

		const filterInput = this.config.filterInput;
		if (filterInput) {
			filterInput.change(() => {
				this.searchOfflineData(filterInput.val() as string);
				this.bulkSelector.selectNone();
			});
		}

		if (this.config.externalUserActionRequest) {
			this.config.element.on("click", ".externalUserActionButton", e => {
				const button = $(e.target as HTMLElement);
				const rowData = this.getRowData(button.closest("tr"));

				Button.setLoading(button);
				this.config.externalUserActionRequest({
					allSelected: false,
					idsExclude: null,
					idsInclude: rowData.id,
					integration: dialog.getSelectedIntegration()
				}).always(() => Button.resetLoading(button));
			});
		}
	}

	protected createBulkSelector(): DataTableBulkSelector<ExternalSyncUser> {
		return new DataTableBulkSelector({dataTable: this.dataTable});
	}

	protected updateSelectedRowsCount() {
		const selectedRowsCount = this.bulkSelector.getSelectedEntriesCount();

		const element = this.config.actionButton || $();

		if (this.bulkSelector.isAllEntriesSelected() && !this.bulkSelector.isNoneSelected()) {
			element
				.removeAttr("disabled")
				.text(messages.withArguments(this.config.allSelectedMessage, selectedRowsCount));
			this.bulkSelector.selectAllCheckbox();
		} else if (this.bulkSelector.isNoneSelected()) {
			element
				.attr("disabled", "disabled")
				.text(messages["user.externalUserSyncDialog.bulkselection.none"]);
			this.bulkSelector.unSelectAllCheckbox();
		} else {
			element
				.removeAttr("disabled")
				.text(messages.withArguments(this.config.nSelectedMessage, selectedRowsCount));
			this.bulkSelector.unSelectAllCheckbox();
		}
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			deferLoading: 0,
			paging: true,
			searching: true,
			rowCallback: (row, data) => {
				const user = data as User;
				if (user.disabled) {
					$(row).addClass("archived");
				}
			},
		};
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "desc"]];
	}
}

class ExternalUsersCreateDataTable extends ExternalUsersDataTable {
	protected getColumnSettings(): TypedColumnSettings<ExternalSyncUser>[] {
		return [
			{
				name: "externalId",
				orderable: false,
				render: BasicDataTable.renderSelectCheckbox,
				width: BasicDataTable.COLUMN_SIZE_CHECKBOX
			},
			{data: "firstName", orderable: true, render: BasicDataTable.renderText},
			{data: "lastName", orderable: true, render: BasicDataTable.renderText},
			{data: "email", orderable: true, render: BasicDataTable.renderText},
			{
				name: "externalId",
				orderable: false,
				visible: this.config.externalUserActionRequest !== null,
				className: "text-left text-nowrap no-padding-left",
				width: ExternalUsersDataTable.COLUMN_SIZE_EXTERNAL_USER_ACTION,
				render: () => `<a class="externalUserActionButton">${messages[this.config.externalUserActionMessage]}</a>`
			}
		];
	}
}

class ExternalUsersLinkDataTable extends ExternalUsersDataTable {
	protected getColumnSettings(): TypedColumnSettings<ExternalSyncUser>[] {
		return [
			{
				name: "externalId",
				orderable: false,
				render: BasicDataTable.renderSelectCheckbox,
				width: BasicDataTable.COLUMN_SIZE_CHECKBOX
			},
			{data: "firstName", orderable: true, render: BasicDataTable.renderText},
			{data: "lastName", orderable: true, render: BasicDataTable.renderText},
			{data: "email", className: "border-right border-right-grey-300", orderable: true, render: BasicDataTable.renderText},
			{data: "userLogin", orderable: true, render: BasicDataTable.renderText},
			{data: "userEmail", orderable: true, render: BasicDataTable.renderText},
			{data: "userFullName", orderable: true, render: BasicDataTable.renderText},
			{
				name: "externalId",
				orderable: false,
				visible: this.config.externalUserActionRequest !== null,
				className: "text-left text-nowrap no-padding-left",
				width: ExternalUsersDataTable.COLUMN_SIZE_EXTERNAL_USER_ACTION,
				render: () => `<a class="externalUserActionButton">${messages[this.config.externalUserActionMessage]}</a>`
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "desc"]];
	}
}


class ExternalUsersUpdateDataTable extends ExternalUsersDataTable {
	protected getColumnSettings(): TypedColumnSettings<ExternalSyncUser>[] {
		return [
			{data: "firstName", orderable: true, render: BasicDataTable.renderText},
			{data: "lastName", orderable: true, render: BasicDataTable.renderText},
			{data: "email", orderable: true, render: BasicDataTable.renderText},
			{
				name: "externalId",
				orderable: false,
				visible: this.config.externalUserActionRequest !== null,
				className: "text-left text-nowrap no-padding-left",
				width: ExternalUsersDataTable.COLUMN_SIZE_EXTERNAL_USER_ACTION,
				render: () => `<a class="externalUserActionButton">${messages[this.config.externalUserActionMessage]}</a>`
			}
		];
	}
}
